import { DEFAULT_PRECIO_MIN, DEFAULT_PRECIO_MAX, DEFAULT_RESULT_FILTERS } from './BuscadorResultFilters';

export const URL_FILTER_KEYS = [
  'nombre_producto',
  'sku',
  'medidas',
  'codigo_barras',
  'vehiculo',
  'id_vehiculo',
  'nombre_marca',
  'id_categoria',
  'homonimos',
  'nombre_producto_not_like',
  'estado_rotacion',
];

const EMPTY_FILTERS = {
  sku: '',
  nombre_producto: '',
  medidas: '',
  codigo_barras: '',
  id_vehiculo: '',
  vehiculo: '',
  nombre_marca: '',
  id_categoria: '',
  homonimos: '',
  nombre_producto_not_like: '',
  estado_rotacion: '',
};

/** null = sin filtro; true = agotado; false = con stock */
export const parseAgotadoValue = (value) => {
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return null;
};

export const normalizeResultFilters = (resultFilters = {}) => {
  const normalized = {
    sort: DEFAULT_RESULT_FILTERS.sort,
    precioMin: DEFAULT_PRECIO_MIN,
    precioMax: DEFAULT_PRECIO_MAX,
    agotado: DEFAULT_RESULT_FILTERS.agotado,
  };

  const validSorts = ['relevance', 'price_asc', 'price_desc', 'alpha_asc', 'alpha_desc'];
  if (validSorts.includes(resultFilters.sort)) {
    normalized.sort = resultFilters.sort;
  }

  const rawMin = Number(resultFilters.precioMin);
  if (Number.isFinite(rawMin)) {
    normalized.precioMin = Math.max(DEFAULT_PRECIO_MIN, Math.min(rawMin, DEFAULT_PRECIO_MAX));
  }

  const rawMax = Number(resultFilters.precioMax);
  if (Number.isFinite(rawMax)) {
    normalized.precioMax = Math.max(DEFAULT_PRECIO_MIN, Math.min(rawMax, DEFAULT_PRECIO_MAX));
  }

  if (normalized.precioMin > normalized.precioMax) {
    normalized.precioMin = DEFAULT_PRECIO_MIN;
    normalized.precioMax = DEFAULT_PRECIO_MAX;
  }

  if (Object.prototype.hasOwnProperty.call(resultFilters, 'agotado')) {
    normalized.agotado = parseAgotadoValue(resultFilters.agotado);
  }

  return normalized;
};

/** Solo incluye precioMin/precioMax cuando el usuario aplicó un filtro de rango activo. */
export const getActivePriceFiltersForPayload = (resultFilters = {}) => {
  const normalized = normalizeResultFilters(resultFilters);
  const payload = {};
  if (normalized.precioMin > DEFAULT_PRECIO_MIN) {
    payload.precioMin = normalized.precioMin;
  }
  if (normalized.precioMax < DEFAULT_PRECIO_MAX) {
    payload.precioMax = normalized.precioMax;
  }
  return payload;
};

/** Incluye agotado en payload solo cuando el switch no está en Neutral. */
export const getActiveAgotadoForPayload = (resultFilters = {}) => {
  const normalized = normalizeResultFilters(resultFilters);
  if (normalized.agotado === true || normalized.agotado === false) {
    return { agotado: normalized.agotado };
  }
  return {};
};

/** id_vehiculo y vehiculo (texto) son mutuamente excluyentes en payload, URL y recientes. */
export const normalizeFiltersForSearchPayload = (filters = {}) => {
  const normalized = { ...filters };
  if (String(normalized.id_vehiculo ?? '').trim() !== '') {
    normalized.vehiculo = '';
  }
  return normalized;
};

/** Solo para UI: muestra el nombre en el input cuando la búsqueda persiste solo id_vehiculo. */
export const enrichFiltersForFormDisplay = (filters = {}, vehiculos = []) => {
  const enriched = { ...filters };
  if (!String(enriched.id_vehiculo ?? '').trim() || String(enriched.vehiculo ?? '').trim()) {
    return enriched;
  }
  const match = vehiculos.find((v) => String(v.id_vehiculo) === String(enriched.id_vehiculo));
  if (match?.vehiculo_completo) {
    enriched.vehiculo = match.vehiculo_completo;
  }
  return enriched;
};

export const parseBuscadorUrl = (searchParams) => {
  const filters = { ...EMPTY_FILTERS };

  URL_FILTER_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value != null && String(value).trim() !== '') {
      filters[key] = value;
    }
  });

  let filtrosAtributos = {};
  const attrRaw = searchParams.get('attr');
  if (attrRaw) {
    try {
      const parsed = JSON.parse(attrRaw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        filtrosAtributos = parsed;
      }
    } catch {
      filtrosAtributos = {};
    }
  }

  const resultFilters = { ...DEFAULT_RESULT_FILTERS };
  const sort = searchParams.get('sort');
  if (sort) resultFilters.sort = sort;

  const precioMinRaw = searchParams.get('precioMin');
  if (precioMinRaw != null && precioMinRaw !== '') {
    const precioMin = Number(precioMinRaw);
    if (Number.isFinite(precioMin)) resultFilters.precioMin = precioMin;
  }

  const precioMaxRaw = searchParams.get('precioMax');
  if (precioMaxRaw != null && precioMaxRaw !== '') {
    const precioMax = Number(precioMaxRaw);
    if (Number.isFinite(precioMax)) resultFilters.precioMax = precioMax;
  }

  const agotadoRaw = searchParams.get('agotado');
  if (agotadoRaw != null && agotadoRaw !== '') {
    resultFilters.agotado = parseAgotadoValue(agotadoRaw);
  }

  const normalizedResultFilters = normalizeResultFilters(resultFilters);

  const normalizedFilters = normalizeFiltersForSearchPayload(filters);

  const hasFilter = URL_FILTER_KEYS.some((key) => String(normalizedFilters[key] || '').trim() !== '');
  const hasAtributos = Object.values(filtrosAtributos).some((v) => String(v || '').trim() !== '');
  const hasResultFilters =
    (normalizedResultFilters.sort && normalizedResultFilters.sort !== DEFAULT_RESULT_FILTERS.sort)
    || normalizedResultFilters.precioMin > DEFAULT_PRECIO_MIN
    || normalizedResultFilters.precioMax < DEFAULT_PRECIO_MAX
    || normalizedResultFilters.agotado === true
    || normalizedResultFilters.agotado === false;

  return {
    filters: normalizedFilters,
    filtrosAtributos,
    resultFilters: normalizedResultFilters,
    hasActiveSearch: hasFilter || hasAtributos || hasResultFilters,
  };
};

export const buildBuscadorUrlParams = (filters, filtrosAtributos = {}, resultFilters = DEFAULT_RESULT_FILTERS) => {
  const normalizedResult = normalizeResultFilters(resultFilters);
  const normalizedFilters = normalizeFiltersForSearchPayload(filters);
  const params = {};

  URL_FILTER_KEYS.forEach((key) => {
    const value = normalizedFilters?.[key];
    if (value != null && String(value).trim() !== '') {
      params[key] = String(value).trim();
    }
  });

  const attrEntries = Object.entries(filtrosAtributos || {}).filter(([, v]) => String(v || '').trim() !== '');
  if (attrEntries.length > 0) {
    params.attr = JSON.stringify(Object.fromEntries(attrEntries));
  }

  if (normalizedResult.sort && normalizedResult.sort !== DEFAULT_RESULT_FILTERS.sort) {
    params.sort = normalizedResult.sort;
  }
  const activePrice = getActivePriceFiltersForPayload(normalizedResult);
  if (activePrice.precioMin != null) {
    params.precioMin = String(activePrice.precioMin);
  }
  if (activePrice.precioMax != null) {
    params.precioMax = String(activePrice.precioMax);
  }
  if (normalizedResult.agotado === true) {
    params.agotado = '1';
  } else if (normalizedResult.agotado === false) {
    params.agotado = '0';
  }

  return params;
};
